import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  FlaskConical,
  LayoutTemplate,
  Loader2,
  Lock,
  Sparkles,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { withBackend, toThumbnailUrl } from '../utils/api';

const MODEL_OPTIONS = [
  { value: 'opus', label: 'Opus (default, best quality)' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'pro', label: 'Pro' },
  { value: 'flash', label: 'Flash' },
];

const MIN_SLIDES = 3;
const MAX_SLIDES = 20;

const clampSlides = (value) => {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return 8;
  return Math.min(MAX_SLIDES, Math.max(MIN_SLIDES, n));
};

// fill_report entries may carry arrays of placeholder names or plain counts.
const formatReportValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const uniquePlaceholderTypes = (placeholders) => {
  const types = [];
  (placeholders || []).forEach((p) => {
    const t = String(p?.type || 'UNKNOWN').toUpperCase();
    if (!types.includes(t)) types.push(t);
  });
  return types;
};

export default function NativeTemplateLab({ userId }) {
  // Step 1 — upload & analyze
  const [file, setFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [template, setTemplate] = useState(null);
  const fileInputRef = useRef(null);

  // Step 3 — generate
  const [prompt, setPrompt] = useState('');
  const [numSlides, setNumSlides] = useState(8);
  const [model, setModel] = useState('opus');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [result, setResult] = useState(null);
  const [showFillReport, setShowFillReport] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Patient-loading elapsed counter for the 30-90s generate call.
  useEffect(() => {
    if (!generating) return undefined;
    setElapsed(0);
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [generating]);

  // Previously analyzed templates — the file and catalog persist server-side,
  // so reopening the lab rehydrates without re-uploading a 50MB master.
  const [savedTemplates, setSavedTemplates] = useState([]);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(withBackend(`/api/native-templates?user_id=${encodeURIComponent(userId)}`));
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setSavedTemplates(data.templates || []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="bg-card border border-border rounded-xl p-10 flex flex-col items-center text-center gap-3">
          <Lock className="w-8 h-8 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Sign in required</h2>
          <p className="text-sm text-muted-foreground">
            The Native Template Lab is tied to your account. Please sign in to upload templates and run experiments.
          </p>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const picked = e.target.files && e.target.files[0];
    setFile(picked || null);
    setAnalyzeError('');
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Choose a .pptx or .potx file first');
      return;
    }
    setAnalyzing(true);
    setAnalyzeError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      formData.append('name', templateName.trim() || file.name.replace(/\.(pptx|potx)$/i, ''));

      const res = await fetch(withBackend('/api/native-templates/analyze'), {
        method: 'POST',
        body: formData,
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }

      if (!res.ok || data.error) {
        let message = data.error || `Analyze failed (HTTP ${res.status})`;
        if (res.status === 503) {
          message = `Backend not set up for native templates: ${message}`;
        } else if (res.status === 413) {
          message = `File too large: ${message}`;
        }
        setAnalyzeError(message);
        toast.error(message);
        return;
      }

      setTemplate(data);
      setResult(null);
      setGenerateError('');
      const usable = data.usable_layout_count ?? (data.layouts || []).filter((l) => (l.placeholders || []).length > 0).length;
      toast.success(`Template analyzed: ${usable} usable layout${usable === 1 ? '' : 's'} found`);
    } catch (err) {
      const message = err?.message || 'Network error while analyzing template';
      setAnalyzeError(message);
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    if (!template?.template_id) {
      toast.error('Analyze a template first');
      return;
    }
    if (!prompt.trim()) {
      toast.error('Describe the deck you want to generate');
      return;
    }
    setGenerating(true);
    setGenerateError('');
    setResult(null);
    try {
      const res = await fetch(withBackend(`/api/native-templates/${template.template_id}/generate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          prompt: prompt.trim(),
          num_slides: clampSlides(numSlides),
          model,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }

      if (!res.ok || data.error || data.success === false) {
        let message = data.error || `Generation failed (HTTP ${res.status})`;
        if (res.status === 503) {
          message = `Backend not set up for native templates: ${message}`;
        }
        setGenerateError(message);
        toast.error(message);
        return;
      }

      setResult(data);
      setShowFillReport(false);
      toast.success(`Deck generated: ${data.slide_count ?? (data.previews || []).length} slides`);
    } catch (err) {
      const message = err?.message || 'Network error while generating deck';
      setGenerateError(message);
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const layouts = template?.layouts || [];

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FlaskConical className="w-6 h-6" />
            Native Template Lab
          </h1>
          <span className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-2.5 py-0.5 text-xs font-medium">
            Experimental
          </span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Upload a PowerPoint master template, and we generate decks by filling its own layouts natively — no
          conversion, perfect fidelity. This is an experiment: judge the results honestly.
        </p>
      </div>

      {/* STEP 1 — Upload */}
      <section className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Step 1 · Upload template</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="ntl-file" className="block text-sm text-foreground">
              Master template (.pptx or .potx)
            </label>
            <input
              id="ntl-file"
              ref={fileInputRef}
              type="file"
              accept=".pptx,.potx"
              onChange={handleFileChange}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (analyzing) return;
                const file = e.dataTransfer?.files?.[0];
                if (file && /\.(pptx|potx)$/i.test(file.name)) {
                  // assign to the real input so the visible control shows the
                  // file name instead of "No file chosen"
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  e.target.files = dt.files;
                  handleFileChange({ target: e.target });
                }
              }}
              disabled={analyzing}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:opacity-80 file:cursor-pointer"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ntl-name" className="block text-sm text-foreground">
              Template name <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="ntl-name"
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. Corporate master v3"
              disabled={analyzing}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={analyzing || !file}
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {analyzing ? 'Analyzing…' : 'Analyze template'}
          </button>
          {template && !analyzing && (
            <span className="text-sm text-muted-foreground">
              Analyzed: <span className="text-foreground font-medium">{template.name}</span>
            </span>
          )}
        </div>
        {analyzeError && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{analyzeError}</span>
          </div>
        )}
        {savedTemplates.length > 0 && (
          <div className="pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Previously analyzed — pick one to skip re-uploading
            </p>
            <div className="flex flex-wrap gap-2">
              {savedTemplates.map((t) => (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => {
                    setTemplate({
                      template_id: t.template_id,
                      name: t.name,
                      layouts: t.layouts || [],
                      usable_layout_count: (t.layouts || []).filter((l) => (l.placeholders || []).length > 0).length,
                    });
                    setResult(null);
                    setGenerateError('');
                    toast.success(`Loaded "${t.name}" — ready to generate`);
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                    template?.template_id === t.template_id
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-accent/40'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* STEP 2 — Layout catalog */}
      {template && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Step 2 · Layout catalog</h2>
            </div>
            <span className="text-xs text-muted-foreground">
              {template.usable_layout_count ?? layouts.filter((l) => (l.placeholders || []).length > 0).length} of{' '}
              {layouts.length} layouts usable
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {layouts.map((layout) => {
              const placeholders = layout.placeholders || [];
              const unusable = placeholders.length === 0;
              const types = uniquePlaceholderTypes(placeholders);
              return (
                <div
                  key={layout.layout_index}
                  className={`border border-border rounded-lg overflow-hidden ${unusable ? 'opacity-50' : ''}`}
                >
                  {layout.thumbnail_url ? (
                    <img
                      src={toThumbnailUrl(layout.thumbnail_url)}
                      alt={layout.name || `Layout ${layout.layout_index}`}
                      className="w-full aspect-video object-contain bg-muted"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full aspect-video bg-muted flex items-center justify-center">
                      <LayoutTemplate className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="p-3 space-y-1">
                    <p className="text-sm font-medium text-foreground truncate" title={layout.name}>
                      {layout.name || `Layout ${layout.layout_index}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {placeholders.length} placeholder{placeholders.length === 1 ? '' : 's'}
                      {unusable && ' · unusable'}
                    </p>
                    {layout.has_baked_content && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        fixed sample content — not offered to the planner
                      </p>
                    )}
                    {types.length > 0 && (
                      <p className="text-[11px] text-muted-foreground truncate" title={types.join(', ')}>
                        {types.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* STEP 3 — Generate */}
      {template && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Step 3 · Generate</h2>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ntl-prompt" className="block text-sm text-foreground">
              What should the deck cover?
            </label>
            <textarea
              id="ntl-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="e.g. A 2026 go-to-market strategy for our new analytics product: market context, positioning, pricing, rollout plan…"
              disabled={generating}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="ntl-slides" className="block text-sm text-foreground">
                Number of slides ({MIN_SLIDES}–{MAX_SLIDES})
              </label>
              <input
                id="ntl-slides"
                type="number"
                min={MIN_SLIDES}
                max={MAX_SLIDES}
                value={numSlides}
                onChange={(e) => setNumSlides(e.target.value)}
                onBlur={() => setNumSlides(clampSlides(numSlides))}
                disabled={generating}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ntl-model" className="block text-sm text-foreground">
                Model
              </label>
              <select
                id="ntl-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={generating}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {MODEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate on this template'}
          </button>
          {generating && (
            <div className="flex items-start gap-3 rounded-md border border-border bg-muted px-4 py-3">
              <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-muted-foreground shrink-0" />
              <div className="text-sm">
                <p className="text-foreground font-medium">Planning content onto your template's layouts…</p>
                <p className="text-muted-foreground mt-0.5">
                  This usually takes 30–90 seconds (one big planning pass, then a native render).
                  {elapsed > 0 && ` Elapsed: ${elapsed}s.`}
                </p>
              </div>
            </div>
          )}
          {generateError && (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{generateError}</span>
            </div>
          )}
        </section>
      )}

      {/* RESULTS */}
      {result && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{result.title || 'Generated deck'}</h2>
              <p className="text-xs text-muted-foreground">
                {result.slide_count ?? (result.previews || []).length} slides
                {result.model_used ? ` · model: ${result.model_used}` : ''}
              </p>
            </div>
            {result.download_url && (
              <a
                href={withBackend(result.download_url)}
                download
                className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
              >
                <Download className="w-4 h-4" />
                Download .pptx
              </a>
            )}
          </div>

          {(result.previews || []).length > 0 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {result.previews.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Slide ${i + 1} preview`}
                  className="aspect-video w-64 shrink-0 object-contain bg-muted border border-border rounded-lg"
                />
              ))}
            </div>
          )}

          {(result.fill_report || []).length > 0 && (
            <div className="border border-border rounded-lg">
              <button
                type="button"
                onClick={() => setShowFillReport((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted rounded-lg"
              >
                {showFillReport ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Fill report ({result.fill_report.length} slides)
              </button>
              {showFillReport && (
                <div className="px-4 pb-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="py-2 pr-4 font-medium">Slide</th>
                        <th className="py-2 pr-4 font-medium">Layout</th>
                        <th className="py-2 pr-4 font-medium">Filled</th>
                        <th className="py-2 font-medium">Missed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.fill_report.map((row, i) => (
                        <React.Fragment key={i}>
                          <tr className="border-b border-border last:border-0 align-top">
                            <td className="py-2 pr-4 text-foreground">{i + 1}</td>
                            <td className="py-2 pr-4 text-foreground">{formatReportValue(row.layout_index)}</td>
                            <td className="py-2 pr-4 text-muted-foreground">{formatReportValue(row.filled)}</td>
                            <td className="py-2 text-muted-foreground">{formatReportValue(row.missed)}</td>
                          </tr>
                          {(row.slide_placeholder_idx || row.planned_idx) && (
                            <tr className="border-b border-border last:border-0">
                              <td colSpan={4} className="pb-2 text-[11px] text-muted-foreground">
                                slide has idx {formatReportValue(row.slide_placeholder_idx)} · planner targeted {formatReportValue(row.planned_idx)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Open the downloaded file in PowerPoint and check fidelity: fonts, colors, master graphics, and placeholder
            positions should match the original template exactly.
          </p>
        </section>
      )}
    </div>
  );
}
